import { useState, useEffect } from "react";
import { useSidebar } from "@/components/ui/sidebar";

/**
 * Custom hook for managing dismissable sidebar popovers with localStorage persistence
 * and collapse-aware open state.
 *
 * @param localStorageKey - The key to use for storing dismissal state in localStorage
 * @param shouldShow - Boolean indicating if the popover should be shown
 * @returns Object with open state, close handler, collapse state, and render flag
 */
export function useDismissableSidebarPopover(localStorageKey: string, shouldShow: boolean) {
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const isCollapsed = sidebarState === "collapsed" && !isMobile;

  // Track popover open state - check localStorage to see if it was dismissed
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") {
      return false; // Default to closed on SSR
    }
    try {
      const dismissed = localStorage.getItem(localStorageKey);
      return dismissed !== "true" && shouldShow;
    } catch {
      return false; // Default to closed on error
    }
  });

  // Close popover when sidebar is collapsed, restore when expanded if not dismissed
  // Also respect the shouldShow condition
  useEffect(() => {
    // Don't show popover if shouldShow condition is false
    if (!shouldShow) {
      setOpen(false);
      return;
    }
    if (isCollapsed) {
      setOpen(false);
    } else {
      // Restore open state from localStorage when sidebar expands
      if (typeof window === "undefined") {
        return;
      }
      try {
        const dismissed_0 = localStorage.getItem(localStorageKey);
        setOpen(dismissed_0 !== "true" && shouldShow);
      } catch {
        // Default to closed on error
        setOpen(false);
      }
    }
  }, [isCollapsed, localStorageKey, shouldShow]);

  // Listen for storage changes (e.g., when popover is dismissed in another tab)
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === localStorageKey) {
        if (e.newValue === "true") {
          setOpen(false);
        } else if (e.newValue === null && shouldShow && !isCollapsed) {
          setOpen(true);
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [localStorageKey, shouldShow, isCollapsed]);
  const handleClose = (e_0: React.MouseEvent) => {
    e_0.preventDefault();
    e_0.stopPropagation();
    setOpen(false);
    // Save to localStorage that user has dismissed the popover
    // Popover will stay closed after dismissal
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(localStorageKey, "true");
      } catch {
        // Silently fail if storage is unavailable
      }
    }
  };
  return {
    open: open && !isCollapsed,
    handleClose,
    isCollapsed,
    shouldRender: shouldShow
  };
}